import find from 'lodash/find';
import union from 'lodash/union';
import { catchError, map } from 'rxjs/operators';
import { resultsConfig } from '@Constants/Results';
import { FEATURES } from '@Constants/rx-ui/constants';
import EmptyResultsException from '@Services/EmptyResultsException';
import Auth from '@Services/Auth';
import ResultsInterface from '@Services/ResultsInterface';
import ResultsPerPageHelper from '@Services/ResultsPerPageHelper';
import * as Results from '@Services/Results';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import EntriesEventBus from './EntriesEventBus';

class Entries {
  /* Private */

  _currentPage = null;

  _limit = null;

  _getRequestParams = ({
    resultName,
    pageNumber,
    databaseId,
    context,
    sort,
    order,
    hitSetKey,
    coResults,
    limit,
    resultSize,
    filterApplied,
    similarityMol,
    semanticSearchIncluded,
    semanticResultName,
    textQuery,
  }) => {
    const firstItem = (pageNumber - 1) * this.limit + 1;
    const pageLimit = firstItem + this.limit - 1;
    const lastItem = resultSize < pageLimit ? resultSize : pageLimit;
    const combinedResponse = Auth.isFeatureEnabled(FEATURES.bundleExtraReactionData);

    return {
      resultName,
      databaseId,
      context,
      firstItem,
      lastItem,
      sort,
      order,
      hitSetKey,
      coResults,
      pageNumber,
      limit,
      combinedResponse,
      filterApplied,
      similarityMol,
      semanticSearchIncluded,
      semanticResultName,
      textQuery,
    };
  };

  /* Public */

  params = null;

  resetParams() {
    this.params = null;
  }

  get limit() {
    if (!this._limit) {
      this._limit = ResultsPerPageHelper.getResultsPerPage();
    }
    return this._limit;
  }

  setLimit(value, temporary = false) {
    if (!value) return;

    this._limit = value;
    if (!temporary) ResultsPerPageHelper.setResultsPerPage(value);
  }

  getPage(params) {
    const processResults = (entities) => {
      EntriesEventBus.emit(EntriesEventBus.EVENTS.resultsChunkLoaded);
      this._currentPage = this._currentPage.concat(entities);
      return entities;
    };
    const processError = (exception) => {
      if (exception instanceof EmptyResultsException) {
        NotifyDialogsService.openModalWindow({
          title: exception.title,
          text: exception.message,
          timestamp: new Date(),
          confirmText: 'Close',
          type: 'error',
          backdrop: 'static',
          trackingOptions: {
            message: `${exception.title} ${exception.message}`,
            component: 'Results',
            prefix: 'se-',
          },
        });
        throw exception;
      } else if (exception instanceof Error) {
        Logger.error(`Entries: unexpected error occurred while getting results`, exception);
      }
    };

    this.params = { ...params, limit: this.limit };

    this._currentPage = [];

    const requestParams = this._getRequestParams(params);
    EntriesEventBus.emit(EntriesEventBus.EVENTS.resultsRequested, requestParams);
    const resultsSub = ResultsInterface.getResults(requestParams);
    return resultsSub.pipe(
      map(({ data }) => processResults(data)),
      catchError(processError)
    );
  }

  next(firstItem) {
    const chunkSize = resultsConfig.chunkSizePerContext[this.params.context];
    const lastItem = firstItem + chunkSize;
    const chunk = this._currentPage.slice(firstItem, lastItem);
    return chunk || [];
  }

  getTargetsDetails = (index, requestParams) => {
    const { databaseId, hitSetKey, order, resultName } = requestParams || this.params;
    const entry = find(this._currentPage, (item) => item.index === index);

    if (entry && entry.targetDetails) {
      return Promise.resolve(entry.targetDetails);
    }

    return Results.getTargetsDetails({ index, databaseId, hitSetKey, order, resultName })
      .then((data) => {
        if (entry) {
          entry.targetDetails = data;
        }
        return data;
      })
      .catch(Logger.reject);
  };

  getReactionDetails = ({ index, resultName, databaseId, firstItem, lastItem, order, sort, hitData }) => {
    const currentItem = find(this._currentPage, { index }) || {};

    if (currentItem.details && currentItem.details.length >= lastItem) {
      const slice = currentItem.details.slice(firstItem - 1, lastItem);

      return Promise.resolve(slice);
    }

    return Results.getResultMoreReactionDetails(index, resultName, databaseId, firstItem, lastItem, order, sort, hitData)
      .then((details) => {
        currentItem.details = union(currentItem.details || [], details);

        return details;
      })
      .catch(Logger.reject);
  };
}

export default new Entries();
